import React from 'react';
import { useAuthStore } from '../stores/auth-store';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, captchaToken: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = React.createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  login: async () => { throw new Error('Not implemented'); },
  logout: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeUser = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const storeLogin = useAuthStore((state) => state.login);
  const storeLogout = useAuthStore((state) => state.logout);

  const user = React.useMemo(() => {
    if (!storeUser) return null;
    return {
      id: storeUser.id.toString(),
      name: storeUser.fullname,
      email: storeUser.email,
      avatar: storeUser.avatar || undefined,
      role: storeUser.role,
    };
  }, [storeUser]);

  const value = React.useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    login: storeLogin,
    logout: storeLogout,
  }), [user, isAuthenticated, isLoading, storeLogin, storeLogout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
